import type { ImageField, RichTextField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, type JSXMapSerializer } from '@prismicio/react';
import { HorizontalContentCard } from '@/components/redesign/HorizontalContentCards';

type Review = {
  profile_photo: ImageField;
  review: RichTextField;
  name: RichTextField;
  stars: number | null;
};

/**
 * Shared inline serializers for the review card's Rich Text fields. `strong`
 * and `em` inherit the parent paragraph's size/color and just add
 * weight/italic; `hyperlink` renders through `PrismicNextLink` (so internal
 * doc links stay client-side) with a subtle purple accent + underline that
 * matches the star color.
 */
const inlineMarks: JSXMapSerializer = {
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  hyperlink: ({ children, node }) => (
    <PrismicNextLink
      field={node.data}
      className="text-[#4B0FEA] underline underline-offset-2"
    >
      {children}
    </PrismicNextLink>
  ),
};

const reviewComponents: JSXMapSerializer = {
  ...inlineMarks,
  paragraph: ({ children }) => (
    <p className="mt-[10px] text-[16px] font-light leading-[1.5] text-black first:mt-0 md:text-[18px]">
      {children}
    </p>
  ),
};

const nameComponents: JSXMapSerializer = {
  ...inlineMarks,
  paragraph: ({ children }) => (
    <p className="text-[14px] font-normal uppercase leading-[1.2] text-[#041118]">
      {children}
    </p>
  ),
};

/**
 * Purple 5-star row. Matches the Figma review card (`#4B0FEA`, 18×16 stars,
 * 4px gap). Falls back to gray for the unfilled slots so partial ratings still
 * read correctly if editors enter fewer than 5.
 */
function StarRating({ count }: { count: number | null }) {
  const filled = Math.min(Math.max(Math.round(count ?? 0), 0), 5);
  return (
    <div className="flex gap-4" aria-label={`${filled} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          width="18"
          height="16"
          viewBox="0 0 18 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M7.86145 0.51869C8.15936 -0.172898 9.17402 -0.172897 9.47193 0.518692L11.2302 4.60057C11.3559 4.89213 11.6398 5.09134 11.9658 5.11657L16.5297 5.46986C17.3029 5.52972 17.6165 6.46147 17.0273 6.94875L13.5502 9.82474C13.3018 10.0302 13.1933 10.3526 13.2692 10.6597L14.3315 14.9599C14.5115 15.6885 13.6907 16.2644 13.0286 15.874L9.12129 13.5695C8.84219 13.4049 8.49119 13.4049 8.2121 13.5695L4.30473 15.874C3.64271 16.2644 2.82184 15.6885 3.00182 14.9599L4.06418 10.6597C4.14006 10.3526 4.03159 10.0302 3.78323 9.82474L0.305988 6.94875C-0.283156 6.46147 0.0303922 5.52972 0.803654 5.46986L5.36759 5.11657C5.69358 5.09134 5.97755 4.89213 6.10314 4.60057L7.86145 0.51869Z"
            fill={i < filled ? '#4B0FEA' : '#D1D5DB'}
          />
        </svg>
      ))}
    </div>
  );
}

/**
 * Card layout for the auto-scrolling testimonial variation of
 * `StackedHeaderCardsRow`. Mirrors `AutoScrollStepCard`'s sizing so the
 * marquee behaves identically (one card ≈ 45vw with min/max clamps), and
 * pairs the same hover lift + shadow so both variations feel like siblings.
 *
 * Content layout matches `FeatureHighlights.withReviews` minus the role
 * field: profile photo → star rating → quote → author name.
 */
export function AutoScrollReviewCard({ review }: { review: Review }) {
  const hasPhoto = isFilled.image(review.profile_photo);
  const hasReview = isFilled.richText(review.review);
  const hasName = isFilled.richText(review.name);

  return (
    <HorizontalContentCard className="flex h-full w-[45vw] min-w-[280px] max-w-[400px] flex-col gap-0 p-6 transition-all duration-300 ease-out hover:-translate-y-[2px] hover:shadow-[0_12px_28px_rgba(0,0,0,0.16)]">
      {hasPhoto && (
        <div className="relative shrink-0 h-[130px] w-[130px] overflow-hidden rounded-[8px]">
          <PrismicNextImage
            field={review.profile_photo}
            fill
            sizes="130px"
            className="object-cover"
            fallbackAlt=""
          />
        </div>
      )}

      <div className="mt-4 first:mt-0">
        <StarRating count={review.stars} />
      </div>

      {hasReview && (
        <PrismicRichText field={review.review} components={reviewComponents} />
      )}

      {/*
        Spacer that (1) enforces the design's 36px minimum gap before the
        name and (2) grows to fill the extra vertical space marquee cards
        inherit from the tallest sibling — so the name stays bottom-aligned
        across every card in the row.
      */}
      {hasName && (
        <>
          <div className="mt-9 grow" aria-hidden="true" />
          <PrismicRichText field={review.name} components={nameComponents} />
        </>
      )}
    </HorizontalContentCard>
  );
}
